import axios from 'axios';
import { omitBy } from 'lodash';
import { unset } from 'lodash';
import { ThunkAction } from 'redux-thunk';
import { CommonRootState } from '../combineReducers';
import {
  IContactInformationStatus,
  IHiringClientsStatus,
  IOperatingDetailsStatus,
  IOrganizationProfileStatus
} from '../interfaces/organizationProfileNotifications';

type ValidationRule = {
  type: string;
  pattern?: string;
  min?: number;
  max?: number;
  exact?: number;
  prefixes?: string[];
  message?: Record<string, string>;
};

type ValidationRules = {
  matchMode?: string;
  rules: ValidationRule[];
  message?: Record<string, string>;
};

type RegistrationNumberTypeConfig = {
  code: string;
  validationRules?: ValidationRules;
};

type RegistrationNumberConfigResponse = {
  registrationNumberTypes: RegistrationNumberTypeConfig[];
};

const CREDIT_SAFE_TYPE_ID = 6;
const CREDIT_SAFE_COUNTRY_CODE = 'US';
const CREDIT_SAFE_CODE = 'CREDITSAFE';

let cachedCreditSafeValidationRules: ValidationRules | null | undefined;

const validateCreditSafeRule = (rule: ValidationRule, value: string): boolean => {
  switch (rule.type) {
    case 'regex':
      return rule.pattern ? new RegExp(rule.pattern).test(value) : false;
    case 'length': {
      const length = value.length;
      if (rule.exact !== undefined) {
        return length === rule.exact;
      }

      const minPassed = rule.min === undefined || length >= rule.min;
      const maxPassed = rule.max === undefined || length <= rule.max;
      return minPassed && maxPassed;
    }
    case 'startsWith':
      return Array.isArray(rule.prefixes) && rule.prefixes.some(prefix => value.startsWith(prefix));
    case 'checksum':
    case 'custom':
      return true;
    default:
      return true;
  }
};

const isCreditSafeValueValid = (value: string, rules: ValidationRules | null | undefined): boolean => {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return false;
  }

  if (!rules?.rules?.length) {
    return true;
  }

  const matchMode = (rules.matchMode || 'all').toLowerCase();
  const results = rules.rules.map(rule => validateCreditSafeRule(rule, normalizedValue));

  return matchMode === 'any' ? results.some(Boolean) : results.every(Boolean);
};

const getCreditSafeValidationRules = async (): Promise<ValidationRules | null> => {
  if (cachedCreditSafeValidationRules !== undefined) {
    return cachedCreditSafeValidationRules;
  }

  try {
    const { data } = await axios.get<RegistrationNumberConfigResponse>(
      `/api/v3.03/countries/${CREDIT_SAFE_COUNTRY_CODE}/registration-number-configs`
    );

    const creditSafeConfig = data.registrationNumberTypes?.find(type => type.code === CREDIT_SAFE_CODE);
    cachedCreditSafeValidationRules = creditSafeConfig?.validationRules ?? null;
  } catch {
    cachedCreditSafeValidationRules = null;
  }

  return cachedCreditSafeValidationRules;
};

export const updateOrganizationProfileNotifications = (
  organizationId: string
): ThunkAction<Promise<IOrganizationProfileStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IOrganizationProfileStatus>(async (resolve, reject) => {
    try {
      const excludeKeys = ['contactInformationSection.branchLocationsIsCompleted'];
      interface IOrganizationWithRegistrationNumbers {
        organizationRegistrationNumbers?: Array<{ typeId: number; value: string | null }>;
      }
      const [pageStatusResponse, organizationResponse] = await Promise.all([
        axios.get<IOrganizationProfileStatus>(`/api/v3.1/organizations/${organizationId}/pageStatus`),
        axios
          .get<IOrganizationWithRegistrationNumbers>(`/api/v3.3/organizations/${organizationId}`)
          .catch(() => ({ data: {} as IOrganizationWithRegistrationNumbers }))
      ]);
      const data = pageStatusResponse.data;
      excludeKeys.forEach(path => {
        unset(data, path);
      });

      const registrationNumbers = organizationResponse.data.organizationRegistrationNumbers || [];
      const creditSafeEntry = registrationNumbers.find(n => n.typeId === CREDIT_SAFE_TYPE_ID);

      if (data.operationDetailsSection) {
        if (creditSafeEntry?.value) {
          const creditSafeValidationRules = await getCreditSafeValidationRules();
          data.operationDetailsSection.creditSafeNumberIsCompleted = isCreditSafeValueValid(
            creditSafeEntry.value,
            creditSafeValidationRules
          );
        } else if (data.operationDetailsSection.creditSafeNumberIsCompleted !== null) {
          data.operationDetailsSection.creditSafeNumberIsCompleted = false;
        }
      }

      dispatch(changeOrganizationProfileNotifications(data));
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const updateOrganizationProfileContactInformationNotifications = (
  organizationId: string
): ThunkAction<Promise<IContactInformationStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IContactInformationStatus>(async (resolve, reject) => {
    try {
      const excludeKeys = ['branchLocationsIsCompleted'];
      const { data } = await axios.get<IContactInformationStatus>(
        `/api/v3.1/organizations/${organizationId}/pageStatus/contactInformation`
      );
      excludeKeys.forEach(path => {
        unset(data, path);
      });
      dispatch(
        changeOrganizationProfileNotifications({
          contactInformationSection: data
        })
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const updateOrganizationProfileOperationDetailsNotifications = (
  organizationId: string,
  extraFields?: Partial<IOperatingDetailsStatus>
): ThunkAction<Promise<IOperatingDetailsStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IOperatingDetailsStatus>(async (resolve, reject) => {
    try {
      const { data } = await axios.get<IOperatingDetailsStatus>(
        `/api/v3.1/organizations/${organizationId}/pageStatus/operationDetails`
      );
      const nonNullData = omitBy(data, value => value === null) as Partial<IOperatingDetailsStatus>;
      if (extraFields) Object.assign(nonNullData, extraFields);
      dispatch(
        changeOrganizationProfileNotifications({
          operationDetailsSection: nonNullData
        })
      );
      resolve({ ...data, ...extraFields });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateOrganizationProfileHiringClientsNotifications = (
  organizationId: string
): ThunkAction<Promise<IHiringClientsStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IHiringClientsStatus>(async (resolve, reject) => {
    try {
      const { data } = await axios.get<IHiringClientsStatus>(
        `/api/v3.1/organizations/${organizationId}/pageStatus/hiringClients`
      );
      dispatch(
        changeOrganizationProfileNotifications({
          hiringClientSection: data
        })
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

type DeepPartialOrganizationProfileStatus = {
  contactInformationSection?: Partial<IContactInformationStatus>;
  operationDetailsSection?: Partial<IOperatingDetailsStatus>;
  hiringClientSection?: Partial<IHiringClientsStatus>;
};

export const changeOrganizationProfileNotifications = (payload: DeepPartialOrganizationProfileStatus) =>
  ({
    type: 'CHANGE_ORGANIZATION_PROFILE_NOTIFICATIONS',
    payload
  } as const);

export type Actions = ReturnType<typeof changeOrganizationProfileNotifications>;
